import React, { FC, useEffect, useMemo } from 'react';

import styled from 'styled-components';

import { UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UIFormItem, { EUIFormItemVariant } from '@aquariuxfintech/aqx-trader-react-ui/form';
import UISelectPicker, { EUISelectPickerVariant } from '@aquariuxfintech/aqx-trader-react-ui/select-picker';
import { showBasicSnackBar } from '@aquariuxfintech/aqx-trader-react-ui/snack-bar';

import { ECustomerType } from '@aqx-core/common/api/account';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { ISignUpBasicValues } from '@aqx-core/common/hooks/form/use-sign-up-crm-form';
import { ACCOUNT_TYPE_OPTIONS, useSignupCrm } from '@aqx-core/common/services/sign-up-crm';
import { signUpCrmViewController } from '@aqx-core/common/services/sign-up-crm/controller';
import { EUISnackBarMessageType } from '@aqx-core/common/services/snackbar';
import { mapCustomerTypeLabelToText } from '@aqx-core/common/utils/auth';

interface ISignUpCrmBasicFormAdditionalFieldsProps {
  setValue: UseFormSetValue<ISignUpBasicValues>;
  customerType: ECustomerType;
  customerTypeError?: string;
  countryId: number;
  countryIdError?: string;
  trigger: UseFormTrigger<ISignUpBasicValues>;
}

const SignUpCrmBasicFormAdditionalFields: FC<ISignUpCrmBasicFormAdditionalFieldsProps> = ({
  setValue,
  customerType,
  customerTypeError,
  countryId,
  countryIdError,
  trigger
}) => {
  const { t } = useTranslation();

  const { countries, loading } = useSignupCrm();
  const accountTypeOptions = ACCOUNT_TYPE_OPTIONS.map((opt) => ({
    label: mapCustomerTypeLabelToText(opt),
    value: opt
  }));

  const countryLabel =
    customerType === ECustomerType.INDIVIDUAL ? t('Country of Residence') : t('Country of Registration');

  const countriesOptions = useMemo(() => {
    return countries.map(({ country, id }) => ({
      label: country,
      value: id
    }));
  }, [countries]);

  useEffect(() => {
    const onError = (message: string) => {
      showBasicSnackBar({
        message,
        messageType: EUISnackBarMessageType.ERROR
      });
    };
    signUpCrmViewController.getCountries(onError);
  }, []);

  return (
    <StyledContainer>
      <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={t('Account Type')} size="large">
        <UISelectPicker<ECustomerType>
          onSelect={(val) => setValue('customerType', val)}
          placeholder={t('Account Type')}
          options={accountTypeOptions}
          selectedValue={customerType}
          error={customerTypeError}
          dataTestId={EDataTestId.SIGN_UP_ACCOUNT_TYPE}
          size="large"
        />
      </UIFormItem>

      <StyledCountryFormItemContainer>
        <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={countryLabel} size="large">
          <UISelectPicker<number>
            onSelect={(val) => {
              setValue('countryId', val);
              trigger('countryId');
            }}
            variant={EUISelectPickerVariant.SEARCHABLE_SELECTOR}
            options={countriesOptions}
            placeholder={countryLabel}
            selectedValue={countryId}
            error={countryIdError}
            disabled={loading}
            dataTestId={EDataTestId.SIGN_UP_COUNTRY_SELECTOR}
            size="large"
            dropDownMaxHeight={200}
            useLabelToSearch
            searchPlaceholder={t('Search')}
          />
        </UIFormItem>
      </StyledCountryFormItemContainer>
    </StyledContainer>
  );
};

export default SignUpCrmBasicFormAdditionalFields;

const StyledContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const StyledCountryFormItemContainer = styled.div`
  width: 336px;
`;
